import React, { useState, useEffect, useRef } from 'react';
import VeylLogo from './VeylLogo';
import { useLanguage } from '../i18n';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'sw', label: 'Swahili' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'id', label: 'Indonesia' },
  { code: 'zh', label: '中文' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'ha', label: 'Hausa' },
];

const GOAL_CATEGORIES = [
  { emoji: '💪', labelKey: 'goalFitness' },
  { emoji: '📚', labelKey: 'goalLearning' },
  { emoji: '💼', labelKey: 'goalBusiness' },
  { emoji: '🎨', labelKey: 'goalCreative' },
  { emoji: '🤝', labelKey: 'goalRelationships' },
  { emoji: '🏡', labelKey: 'goalHomeLife' },
  { emoji: '💰', labelKey: 'goalMoney' },
  { emoji: '📞', labelKey: 'goalStayingConnected' },
  { emoji: '🧭', labelKey: 'goalIndependentLiving' },
  { emoji: '📱', labelKey: 'goalTechConfidence' },
];

const styleTag = `
  @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
  @keyframes float  { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-12px); } }
  @keyframes floatR { 0%,100% { transform:translateY(0); } 50% { transform:translateY(12px); } }
  @keyframes pulse  { 0%,100% { opacity:1; } 50% { opacity:0.45; } }
  @keyframes sbA    { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-20px); } }
  @keyframes sbB    { 0%,100% { transform:translateY(0); } 50% { transform:translateY(18px); } }

  .veyl-blob-a { animation: sbA 9s ease-in-out infinite; }
  .veyl-blob-b { animation: sbB 11s ease-in-out infinite; }

  .veyl-btn-primary  { transition: transform .2s ease, box-shadow .2s ease !important; }
  .veyl-btn-primary:hover  { transform: translateY(-2px) !important; box-shadow: 0 12px 30px rgba(0,128,129,.5) !important; }
  .veyl-btn-secondary:hover { transform: translateY(-2px) !important; background-color: rgba(255,255,255,.13) !important; }
  .veyl-card:hover   { transform: translateY(-4px) !important; box-shadow: 0 20px 40px rgba(0,0,0,.3) !important; }
  .veyl-feat-card:hover { transform: translateY(-4px) !important; border-color: rgba(0,128,129,.4) !important; }
  .veyl-goal-pill:hover { background-color: rgba(0,128,129,.1) !important; border-color: rgba(0,128,129,.4) !important; color: #00c8c8 !important; }
  .veyl-faq-btn:hover { background-color: rgba(0,128,129,.08) !important; }
  .veyl-lang-btn:hover { background-color: rgba(0,128,129,.2) !important; }
  .veyl-step:hover .veyl-step-number { transform: scale(1.1) !important; box-shadow: 0 0 20px rgba(0,128,129,.5) !important; }
  .veyl-install-btn:hover { background-color: rgba(0,128,129,.28) !important; }

  .veyl-word { display:inline-block; overflow:hidden; vertical-align:bottom; line-height:1.3; }
  .veyl-char { display:inline-block; will-change:transform,opacity; }

  /* Force the hero wordmark SVG to scale to its container's actual width
     instead of a fixed internal pixel size — fixes overflow/clipping on
     narrow mobile viewports. */
  .veyl-logo-wrap { display:flex; justify-content:center; }
  .veyl-logo-wrap svg { width:100% !important; height:auto !important; max-width:100%; }
`;

function loadScript(src) {
  return new Promise(resolve => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src; s.onload = resolve;
    document.head.appendChild(s);
  });
}

function splitText(el, cls) {
  const raw = el.textContent.trim();
  el.innerHTML = '';
  raw.split(' ').forEach((word, wi, arr) => {
    const wSpan = document.createElement('span');
    wSpan.className = 'veyl-word';
    word.split('').forEach(ch => {
      const s = document.createElement('span');
      s.className = `veyl-char ${cls}`;
      s.textContent = ch;
      s.style.cssText = 'opacity:0;transform:translateY(110%);';
      wSpan.appendChild(s);
    });
    el.appendChild(wSpan);
    if (wi < arr.length - 1) {
      const sp = document.createElement('span');
      sp.style.cssText = 'display:inline-block;width:.28em;';
      el.appendChild(sp);
    }
  });
  return el.querySelectorAll(`.${cls}`);
}

// ── PWA install helpers (mobile only) ──
function isMobileDevice() {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}
function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandaloneDisplay() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export default function LandingPage({ onGetStarted }) {
  const { t, language, setLanguage } = useLanguage();
  const [openFaq, setOpenFaq]           = useState(null);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [heroVisible, setHeroVisible]   = useState(false);

  // ── PWA install state ──
  const [installPromptEvent, setInstallPromptEvent]             = useState(null);
  const [showInstallButton, setShowInstallButton]               = useState(false);
  const [showDesktopInstallButton, setShowDesktopInstallButton] = useState(false);
  const [showIOSModal, setShowIOSModal]                         = useState(false);

  useEffect(() => {
    setTimeout(() => setHeroVisible(true), 120);
  }, []);

  useEffect(() => {
    // Never show any install button once the app is already running
    // installed (standalone display mode).
    if (isStandaloneDisplay()) return;

    if (isMobileDevice() && isIOSDevice()) {
      // iOS/Safari has no beforeinstallprompt event — show the mobile
      // button right away; clicking it opens manual instructions instead.
      setShowInstallButton(true);
      return;
    }

    // Everyone else (Android via Chrome, and desktop Chrome/Edge) gets a
    // real native install prompt fired as beforeinstallprompt. Desktop
    // Safari/Firefox don't support this event, so no button appears there.
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
      if (isMobileDevice()) {
        setShowInstallButton(true);
      } else {
        setShowDesktopInstallButton(true);
      }
    };
    const handleAppInstalled = () => {
      setShowInstallButton(false);
      setShowDesktopInstallButton(false);
      setInstallPromptEvent(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOSDevice()) {
      setShowIOSModal(true);
      return;
    }
    if (installPromptEvent) {
      installPromptEvent.prompt();
      const { outcome } = await installPromptEvent.userChoice;
      setInstallPromptEvent(null);
      if (outcome === 'accepted') setShowInstallButton(false);
    }
  };

  useEffect(() => {
    // Kill any existing ScrollTriggers before re-running
    if (window.ScrollTrigger) {
      window.ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    }

    Promise.all([
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js'),
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js'),
    ]).then(() => {
      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger;
      if (!gsap || !ScrollTrigger) return;
      gsap.registerPlugin(ScrollTrigger);

      const STYLES = {
        up:   (c, d) => gsap.fromTo(c, { opacity:0, y:'100%' },                      { opacity:1, y:'0%',    duration:.48, delay:d, ease:'power3.out' }),
        skew: (c, d) => gsap.fromTo(c, { opacity:0, y:'80%', skewX:12, rotation:3 }, { opacity:1, y:'0%',    skewX:0, rotation:0, duration:.5,  delay:d, ease:'power3.out' }),
        blur: (c, d) => gsap.fromTo(c, { opacity:0, filter:'blur(7px)', y:'50%' },   { opacity:1, filter:'blur(0px)', y:'0%', duration:.52, delay:d, ease:'power2.out' }),
        wave: (c, d) => gsap.fromTo(c, { opacity:0, y:'85%', rotation:-5 },          { opacity:1, y:'0%',    rotation:0, duration:.48, delay:d, ease:'elastic.out(1,.75)' }),
        drop: (c, d) => gsap.fromTo(c, { opacity:0, y:'-65%', scaleY:1.3 },          { opacity:1, y:'0%',    scaleY:1,   duration:.44, delay:d, ease:'bounce.out' }),
      };

      function charsIn(chars, style) {
        chars.forEach((c, i) => (STYLES[style] || STYLES.up)(c, i * 0.027));
      }
      function charsOut(chars, dir) {
        const y = dir === 'up' ? '-55%' : '55%';
        chars.forEach((c, i) => gsap.to(c, { opacity:0, y, duration:.2, delay:i * 0.01, ease:'power2.in' }));
      }

      function revEl(el, delay = 0) {
        if (!el) return;
        gsap.set(el, { opacity:0, y:20 });
        ScrollTrigger.create({
          trigger: el, start:'top 89%', end:'bottom 4%',
          onEnter:     () => gsap.to(el, { opacity:1, y:0, duration:.55, delay,          ease:'power2.out' }),
          onLeave:     () => gsap.to(el, { opacity:0, y:-16, duration:.22 }),
          onEnterBack: () => gsap.to(el, { opacity:1, y:0, duration:.5,  delay:delay*.5, ease:'power2.out' }),
          onLeaveBack: () => gsap.to(el, { opacity:0, y:18, duration:.22 }),
        });
      }

      function revTitle(el, style) {
        if (!el) return;
        const uid = Math.random().toString(36).slice(2);
        const chars = splitText(el, `vc_${uid}`);
        ScrollTrigger.create({
          trigger: el, start:'top 87%', end:'bottom 4%',
          onEnter:     () => charsIn(chars, style),
          onLeave:     () => charsOut(chars, 'up'),
          onEnterBack: () => charsIn(chars, style),
          onLeaveBack: () => charsOut(chars, 'down'),
        });
      }

      // Hero title — restore raw text first (in case GSAP already split it),
      // then re-split with the current language text
      const line1El = document.getElementById('veyl-h1-l1');
      const line2El = document.getElementById('veyl-h1-l2');
      if (line1El && line2El) {
        // Reset to plain text before splitting (React already rendered t() values)
        line1El.innerHTML = line1El.textContent;
        line2El.innerHTML = line2El.textContent;
        const l1 = splitText(line1El, 'vhc1');
        const l2 = splitText(line2El, 'vhc2');
        const tl = gsap.timeline({ delay:.3 });
        tl.add(() => charsIn(l1, 'up'))
          .add(() => charsIn(l2, 'up'), '-=.25');
      }

      revTitle(document.getElementById('veyl-prob-title'), 'skew');
      document.querySelectorAll('.veyl-problem-card').forEach((c, i) => {
        gsap.set(c, { opacity:0, x:-28 });
        ScrollTrigger.create({
          trigger: c, start:'top 89%', end:'bottom 4%',
          onEnter:     () => gsap.to(c, { opacity:1, x:0,   duration:.6,  delay:i*.12, ease:'power2.out' }),
          onLeave:     () => gsap.to(c, { opacity:0, x:28,  duration:.22 }),
          onEnterBack: () => gsap.to(c, { opacity:1, x:0,   duration:.5,  delay:i*.07 }),
          onLeaveBack: () => gsap.to(c, { opacity:0, x:-28, duration:.22 }),
        });
      });
      revEl(document.getElementById('veyl-diff'), .1);

      revTitle(document.getElementById('veyl-how-title'), 'blur');
      document.querySelectorAll('.veyl-step-card').forEach((c, i) => {
        gsap.set(c, { opacity:0, y:24 });
        ScrollTrigger.create({
          trigger: c, start:'top 89%', end:'bottom 4%',
          onEnter:     () => gsap.to(c, { opacity:1, y:0,   duration:.55, delay:i*.09, ease:'power2.out' }),
          onLeave:     () => gsap.to(c, { opacity:0, y:-18, duration:.22 }),
          onEnterBack: () => gsap.to(c, { opacity:1, y:0,   duration:.45, delay:i*.06 }),
          onLeaveBack: () => gsap.to(c, { opacity:0, y:22,  duration:.22 }),
        });
      });

      revTitle(document.getElementById('veyl-feat-title'), 'drop');
      document.querySelectorAll('.veyl-feat-card').forEach((c, i) => {
        gsap.set(c, { opacity:0, y:28 });
        ScrollTrigger.create({
          trigger: c, start:'top 89%', end:'bottom 4%',
          onEnter:     () => gsap.to(c, { opacity:1, y:0,   duration:.55, delay:(i%3)*.1,  ease:'power2.out' }),
          onLeave:     () => gsap.to(c, { opacity:0, y:-22, duration:.22 }),
          onEnterBack: () => gsap.to(c, { opacity:1, y:0,   duration:.45, delay:(i%3)*.07 }),
          onLeaveBack: () => gsap.to(c, { opacity:0, y:26,  duration:.22 }),
        });
      });

      revTitle(document.getElementById('veyl-goals-title'), 'wave');
      revEl(document.getElementById('veyl-goals-sub'), .08);
      document.querySelectorAll('.veyl-goal-pill').forEach((p, i) => {
        gsap.set(p, { opacity:0, y:16, scale:.95 });
        ScrollTrigger.create({
          trigger: '.veyl-goals-grid', start:'top 86%', end:'bottom 4%',
          onEnter:     () => gsap.to(p, { opacity:1, y:0,   scale:1,   duration:.42, delay:i*.065, ease:'back.out(1.4)' }),
          onLeave:     () => gsap.to(p, { opacity:0, y:-14, scale:.96, duration:.2 }),
          onEnterBack: () => gsap.to(p, { opacity:1, y:0,   scale:1,   duration:.38, delay:i*.04 }),
          onLeaveBack: () => gsap.to(p, { opacity:0, y:16,  scale:.96, duration:.2 }),
        });
      });

      revTitle(document.getElementById('veyl-price-title'), 'up');
      document.querySelectorAll('.veyl-price-card').forEach((c, i) => {
        gsap.set(c, { opacity:0, y:32 });
        ScrollTrigger.create({
          trigger: '.veyl-pricing-grid', start:'top 84%', end:'bottom 4%',
          onEnter:     () => gsap.to(c, { opacity:1, y:0,   duration:.65, delay:i*.16, ease:'power2.out' }),
          onLeave:     () => gsap.to(c, { opacity:0, y:-24, duration:.25 }),
          onEnterBack: () => gsap.to(c, { opacity:1, y:0,   duration:.52, delay:i*.1 }),
          onLeaveBack: () => gsap.to(c, { opacity:0, y:30,  duration:.25 }),
        });
      });

      revTitle(document.getElementById('veyl-faq-title'), 'skew');
      document.querySelectorAll('.veyl-faq-item').forEach((item, i) => {
        gsap.set(item, { opacity:0, y:18 });
        ScrollTrigger.create({
          trigger: item, start:'top 89%', end:'bottom 4%',
          onEnter:     () => gsap.to(item, { opacity:1, y:0,   duration:.5,  delay:i*.09, ease:'power2.out' }),
          onLeave:     () => gsap.to(item, { opacity:0, y:-16, duration:.22 }),
          onEnterBack: () => gsap.to(item, { opacity:1, y:0,   duration:.42, delay:i*.05 }),
          onLeaveBack: () => gsap.to(item, { opacity:0, y:18,  duration:.22 }),
        });
      });

      revTitle(document.getElementById('veyl-safe-title'), 'blur');
      document.querySelectorAll('.veyl-safe-card').forEach((c, i) => {
        gsap.set(c, { opacity:0, y:22 });
        ScrollTrigger.create({
          trigger: '.veyl-safe-grid', start:'top 86%', end:'bottom 4%',
          onEnter:     () => gsap.to(c, { opacity:1, y:0,   duration:.52, delay:i*.1,  ease:'power2.out' }),
          onLeave:     () => gsap.to(c, { opacity:0, y:-18, duration:.22 }),
          onEnterBack: () => gsap.to(c, { opacity:1, y:0,   duration:.44, delay:i*.07 }),
          onLeaveBack: () => gsap.to(c, { opacity:0, y:20,  duration:.22 }),
        });
      });

      revTitle(document.getElementById('veyl-cta-title'), 'wave');
      revEl(document.getElementById('veyl-cta-p'), .1);
      revEl(document.getElementById('veyl-cta-btn'), .18);
    });

    return () => {
      if (window.ScrollTrigger) window.ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [language]); // ← re-runs on every language change

  const faqs = [
    { q: t('faq1q'), a: t('faq1a') },
    { q: t('faq2q'), a: t('faq2a') },
    { q: t('faq3q'), a: t('faq3a') },
    { q: t('faq4q'), a: t('faq4a') },
  ];
  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  const blob = (w, h, color, extra) => ({
    position:'absolute', width:w, height:h, borderRadius:'50%',
    background:`radial-gradient(circle, ${color} 0%, transparent 70%)`,
    filter:'blur(55px)', pointerEvents:'none', zIndex:0, ...extra,
  });

  return (
    <div style={{ minHeight:'100vh', backgroundColor:'#080f1a', fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', overflowX:'hidden', WebkitFontSmoothing:'antialiased' }}>
      <style>{styleTag}</style>

      {/* ── NAV ── */}
      <nav style={{ position:'sticky', top:0, zIndex:100, backgroundColor:'rgba(8,15,26,0.9)', backdropFilter:'blur(20px)', borderBottom:'1px solid rgba(255,255,255,0.06)', padding:'.875rem 2rem', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'.5rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'.75rem', flexWrap:'wrap' }}>
          <VeylLogo width={100} showSpark={false} />
          <div style={{ position:'relative' }}>
            <button className="veyl-btn-secondary" onClick={() => setShowLangMenu(!showLangMenu)}
              style={{ backgroundColor:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'.5rem', padding:'.4rem .875rem', color:'#fff', cursor:'pointer', fontSize:'.8rem', display:'flex', alignItems:'center', gap:'.4rem', transition:'background-color .2s' }}>
              🌐 {currentLang.label} <span style={{ fontSize:'.6rem', opacity:.7 }}>▼</span>
            </button>
            {showLangMenu && (
              <div style={{ position:'absolute', left:0, top:'110%', backgroundColor:'#0f1c2e', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'.75rem', zIndex:100, minWidth:'170px', boxShadow:'0 16px 40px rgba(0,0,0,0.6)', overflow:'hidden', animation:'fadeIn .15s ease' }}>
                {LANGUAGES.map(lang => (
                  <button key={lang.code} className="veyl-lang-btn"
                    onClick={() => { setLanguage(lang.code); setShowLangMenu(false); }}
                    style={{ width:'100%', padding:'.6rem 1rem', textAlign:'left', background: language === lang.code ? 'rgba(0,128,129,0.25)' : 'transparent', border:'none', color: language === lang.code ? '#00d4d4' : 'rgba(255,255,255,0.8)', cursor:'pointer', fontSize:'.85rem', fontWeight: language === lang.code ? '600' : '400', transition:'background-color .15s' }}>
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'.75rem', flexWrap:'wrap' }}>
          {showDesktopInstallButton && (
            <button
              className="veyl-install-btn"
              onClick={handleInstallClick}
              title={t('installTooltip')}
              style={{ display:'flex', alignItems:'center', gap:'.4rem', backgroundColor:'rgba(0,128,129,0.15)', border:'1px solid rgba(0,128,129,0.35)', borderRadius:'.5rem', padding:'.5rem 1rem', color:'#00d4d4', cursor:'pointer', fontSize:'.8rem', fontWeight:'600', transition:'background-color .2s' }}
            >
              💻 {t('installAppLabel')}
            </button>
          )}
          <button className="veyl-btn-primary" onClick={onGetStarted}
            style={{ padding:'.5rem 1.25rem', background:'linear-gradient(135deg,#008081,#1B2D44)', color:'#fff', border:'none', borderRadius:'.5rem', fontWeight:'600', fontSize:'.875rem', cursor:'pointer', boxShadow:'0 4px 15px rgba(0,128,129,0.3)' }}>
            {t('getStarted')}
          </button>
        </div>
      </nav>

      {/* ── iOS install instructions modal ── */}
      {showIOSModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowIOSModal(false); }}
          style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(8,15,26,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'1rem' }}
        >
          <div style={{ backgroundColor:'#0f1c2e', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'1rem', padding:'1.75rem', maxWidth:'360px', width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.5)' }}>
            <h3 style={{ color:'#fff', fontSize:'1.1rem', fontWeight:'700', marginBottom:'1rem' }}>{t('installModalTitle')}</h3>
            {[t('installStep1'), t('installStep2'), t('installStep3')].map((step, i) => (
              <div key={i} style={{ display:'flex', gap:'.75rem', alignItems:'flex-start', marginBottom:'.875rem' }}>
                <div style={{ width:'22px', height:'22px', borderRadius:'50%', backgroundColor:'#4B2C5E', color:'#fff', fontSize:'.72rem', fontWeight:'700', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{i + 1}</div>
                <p style={{ color:'rgba(255,255,255,0.75)', fontSize:'.88rem', lineHeight:1.5, margin:0 }}>{step}</p>
              </div>
            ))}
            <button
              onClick={() => setShowIOSModal(false)}
              style={{ width:'100%', marginTop:'.5rem', padding:'.7rem', backgroundColor:'transparent', color:'#00c8c8', border:'1.5px solid rgba(0,128,129,0.4)', borderRadius:'.6rem', fontWeight:'600', cursor:'pointer', fontSize:'.85rem' }}
            >
              {t('installGotIt')}
            </button>
          </div>
        </div>
      )}

      {/* ── HERO ── */}
      <section style={{ minHeight:'92vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'4rem 1.5rem', position:'relative', overflow:'hidden' }}>
        <div style={blob('420px','420px','rgba(75,44,94,0.38)',  { top:'8%',  left:'12%',  animation:'float 8s ease-in-out infinite' })} />
        <div style={blob('360px','360px','rgba(0,128,129,0.3)',  { bottom:'12%', right:'8%', animation:'floatR 10s ease-in-out infinite' })} />
        <div style={blob('600px','600px','rgba(27,45,68,0.18)',  { top:'50%', left:'50%',  transform:'translate(-50%,-50%)' })} />

        <div style={{ display:'inline-flex', alignItems:'center', gap:'.5rem', backgroundColor:'rgba(0,128,129,0.12)', border:'1px solid rgba(0,128,129,0.3)', borderRadius:'9999px', padding:'.35rem 1rem', marginBottom:'2rem', fontSize:'.8rem', color:'#00c8c8', fontWeight:'600', opacity: heroVisible ? 1 : 0, transition:'opacity .6s ease', position:'relative', zIndex:1 }}>
          <span style={{ width:'6px', height:'6px', borderRadius:'50%', backgroundColor:'#00c8c8', animation:'pulse 2s infinite', display:'inline-block' }} />
          {t('statAnonymous')} · {t('taglineHonest')} · {t('taglineEffective')}
        </div>

        <div className="veyl-logo-wrap" style={{ width:'100%', maxWidth:'420px', marginBottom:'2rem', opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(28px)', transition:'opacity .7s ease, transform .7s ease', position:'relative', zIndex:1 }}>
          <VeylLogo width={420} centered showSpark={false} />
        </div>

        <h1 id="veyl-hero-h1" style={{ fontSize:'clamp(2rem,5vw,3.5rem)', fontWeight:'800', lineHeight:1.15, marginBottom:'1.5rem', maxWidth:'750px', color:'#ffffff', position:'relative', zIndex:1 }}>
          <span id="veyl-h1-l1">{t('heroLine1')}</span>
          <br />
          <span id="veyl-h1-l2" style={{ fontWeight:'300', opacity:.85, fontSize:'.84em' }}>{t('heroLine2')}</span>
        </h1>

        <p style={{ fontSize:'clamp(1rem,2vw,1.2rem)', color:'rgba(255,255,255,0.55)', maxWidth:'600px', lineHeight:1.72, marginBottom:'2.5rem', opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(20px)', transition:'opacity .8s ease .2s, transform .8s ease .2s', position:'relative', zIndex:1 }}>
          {t('landingSubtitle')}
        </p>

        <div style={{ display:'flex', gap:'1rem', flexWrap:'wrap', justifyContent:'center', opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(18px)', transition:'opacity .8s ease .3s, transform .8s ease .3s', position:'relative', zIndex:1 }}>
          <button className="veyl-btn-primary" onClick={onGetStarted}
            style={{ padding:'1rem 2.5rem', background:'linear-gradient(135deg,#008081 0%,#005f5f 100%)', color:'#fff', border:'none', borderRadius:'.75rem', fontSize:'1.05rem', fontWeight:'700', cursor:'pointer', boxShadow:'0 8px 25px rgba(0,128,129,0.4)', letterSpacing:'.01em' }}>
            {t('getStarted')} →
          </button>
          <button className="veyl-btn-secondary"
            onClick={() => document.getElementById('veyl-how-section').scrollIntoView({ behavior:'smooth' })}
            style={{ padding:'1rem 2rem', backgroundColor:'rgba(255,255,255,0.07)', color:'rgba(255,255,255,0.88)', border:'1px solid rgba(255,255,255,0.16)', borderRadius:'.75rem', fontSize:'1.05rem', fontWeight:'600', cursor:'pointer', transition:'background-color .2s' }}>
            {t('howItWorks')}
          </button>
        </div>

        {showInstallButton && (
          <button
            className="veyl-install-btn"
            onClick={handleInstallClick}
            title={t('installTooltip')}
            style={{ display:'flex', alignItems:'center', gap:'.4rem', marginTop:'1.25rem', backgroundColor:'rgba(0,128,129,0.15)', border:'1px solid rgba(0,128,129,0.35)', borderRadius:'.5rem', padding:'.5rem 1rem', color:'#00d4d4', cursor:'pointer', fontSize:'.85rem', fontWeight:'600', transition:'background-color .2s', opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(18px)', position:'relative', zIndex:1 }}
          >
            📱 {t('installAppLabel')} <span style={{ fontSize:'.7rem', fontWeight:'500', color:'rgba(0,212,212,0.65)' }}>· {t('installMobileOnly')}</span>
          </button>
        )}

        <div style={{ display:'flex', gap:'3rem', marginTop:'4rem', flexWrap:'wrap', justifyContent:'center', opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(18px)', transition:'opacity .8s ease .4s, transform .8s ease .4s', position:'relative', zIndex:1 }}>
          {[{ value:'100%', labelKey:'statAnonymous' }, { value:'13', labelKey:'statLanguages' }, { value:'$0', labelKey:'statToStart' }].map((s, i) => (
            <div key={i} style={{ textAlign:'center' }}>
              <div style={{ fontSize:'1.75rem', fontWeight:'800', color:'#00c8c8' }}>{s.value}</div>
              <div style={{ fontSize:'.78rem', color:'rgba(255,255,255,0.45)', marginTop:'.2rem', textTransform:'uppercase', letterSpacing:'.09em' }}>{t(s.labelKey)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <section style={{ padding:'5rem 1.5rem', position:'relative', overflow:'hidden', background:'linear-gradient(180deg,#080f1a 0%,#0d1825 50%,#080f1a 100%)' }}>
        <div className="veyl-blob-a" style={blob('380px','380px','rgba(0,128,129,0.18)', { top:'10%', left:'-80px' })} />
        <div className="veyl-blob-b" style={blob('320px','320px','rgba(75,44,94,0.22)',  { top:'30%', right:'-60px' })} />
        <div style={{ maxWidth:'800px', margin:'0 auto', position:'relative', zIndex:1 }}>
          <div style={{ textAlign:'center', marginBottom:'3rem' }}>
            <h2 id="veyl-prob-title" style={{ fontSize:'clamp(1.75rem,4vw,2.5rem)', fontWeight:'800', color:'#fff', lineHeight:1.22 }}>{t('problemTitle')}</h2>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
            {[t('problem1'), t('problem2'), t('problem3')].map((text, i) => (
              <div key={i} className="veyl-problem-card veyl-card" style={{ padding:'1.5rem', backgroundColor:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderLeft:'3px solid #008081', borderRadius:'.75rem', transition:'transform .2s, background-color .2s' }}>
                <p style={{ fontSize:'1.05rem', color:'rgba(255,255,255,0.78)', margin:0, lineHeight:1.72 }}>{text}</p>
              </div>
            ))}
          </div>
          <div id="veyl-diff" style={{ textAlign:'center', marginTop:'2.5rem', fontSize:'1.35rem', fontWeight:'800', background:'linear-gradient(135deg,#008081,#4B2C5E)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>
            {t('veylIsDifferent')}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="veyl-how-section" style={{ padding:'5rem 1.5rem', position:'relative', overflow:'hidden' }}>
        <div className="veyl-blob-a" style={blob('380px','380px','rgba(0,128,129,0.18)', { top:'20%', left:'-80px' })} />
        <div className="veyl-blob-b" style={blob('320px','320px','rgba(75,44,94,0.22)',  { top:'50%', right:'-60px' })} />
        <div style={{ maxWidth:'800px', margin:'0 auto', position:'relative', zIndex:1 }}>
          <div style={{ textAlign:'center', marginBottom:'3.5rem' }}>
            <h2 id="veyl-how-title" style={{ fontSize:'clamp(1.75rem,4vw,2.5rem)', fontWeight:'800', color:'#fff', lineHeight:1.22 }}>{t('howItWorks')}</h2>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>
            {[
              { n:1, titleKey:'step1Title',       descKey:'step1Desc',        color:'#008081' },
              { n:2, titleKey:'step2Title',       descKey:'step2Desc',        color:'#1B2D44' },
              { n:3, titleKey:'step3Title',       descKey:'step3Desc',        color:'#4B2C5E' },
              { n:4, titleKey:'buildTrust',       descKey:'unveilIdentity',   color:'#008081' },
              { n:5, titleKey:'actuallyAchieve',  descKey:'makeRealProgress', color:'#1B2D44' },
            ].map(({ n, titleKey, descKey, color }) => (
              <div key={n} className="veyl-step veyl-step-card" style={{ display:'flex', gap:'1.25rem', alignItems:'flex-start', padding:'1.5rem', backgroundColor:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'1rem', transition:'transform .2s, box-shadow .2s', cursor:'default' }}>
                <div className="veyl-step-number" style={{ width:'3rem', height:'3rem', background:`linear-gradient(135deg,${color},${color}88)`, color:'#fff', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', fontWeight:'800', flexShrink:0, transition:'transform .2s, box-shadow .2s', boxShadow:`0 4px 15px ${color}44` }}>{n}</div>
                <div>
                  <h3 style={{ fontSize:'1.1rem', fontWeight:'700', color:'#fff', marginBottom:'.4rem' }}>{t(titleKey)}</h3>
                  <p style={{ color:'rgba(255,255,255,0.55)', lineHeight:1.72, margin:0, fontSize:'.95rem' }}>{t(descKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section style={{ padding:'5rem 1.5rem', position:'relative', overflow:'hidden', background:'linear-gradient(180deg,#080f1a 0%,rgba(0,128,129,0.06) 50%,#080f1a 100%)' }}>
        <div className="veyl-blob-a" style={blob('380px','380px','rgba(0,128,129,0.18)', { top:'5%',  left:'-80px' })} />
        <div className="veyl-blob-b" style={blob('320px','320px','rgba(75,44,94,0.22)',  { top:'50%', right:'-60px' })} />
        <div style={{ maxWidth:'1000px', margin:'0 auto', position:'relative', zIndex:1 }}>
          <h2 id="veyl-feat-title" style={{ fontSize:'clamp(1.75rem,4vw,2.5rem)', fontWeight:'800', color:'#fff', lineHeight:1.22, marginBottom:'3rem' }}>{t('featuresTitle')}</h2>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:'1.25rem' }}>
            {[
              { icon:'👤', titleKey:'feature1Title', descKey:'feature1Desc', teal:true  },
              { icon:'🎙️', titleKey:'feature2Title', descKey:'feature2Desc', teal:false },
              { icon:'🔞', titleKey:'feature3Title', descKey:'feature3Desc', teal:true  },
              { icon:'🌍', titleKey:'feature4Title', descKey:'feature4Desc', teal:false },
              { icon:'📋', titleKey:'feature5Title', descKey:'feature5Desc', teal:true  },
              { icon:'👥', titleKey:'feature6Title', descKey:'feature6Desc', teal:false },
              { icon:'🪞', titleKey:'feature7Title', descKey:'feature7Desc', teal:true  },
              { icon:'💬', titleKey:'feature8Title', descKey:'feature8Desc', teal:false },
            ].map((f, i) => (
              <div key={i} className="veyl-feat-card" style={{ backgroundColor:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'1rem', padding:'1.75rem 1.5rem', transition:'transform .2s, border-color .3s' }}>
                <div style={{ width:'44px', height:'44px', borderRadius:'10px', background: f.teal ? 'rgba(0,128,129,0.15)' : 'rgba(75,44,94,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem', marginBottom:'1rem' }}>{f.icon}</div>
                <h3 style={{ fontSize:'.98rem', fontWeight:'700', color:'#fff', marginBottom:'.5rem' }}>{t(f.titleKey)}</h3>
                <p style={{ color:'rgba(255,255,255,0.55)', fontSize:'.88rem', lineHeight:1.65, margin:0 }}>{t(f.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GOAL CATEGORIES ── */}
      <section style={{ padding:'5rem 1.5rem', position:'relative', overflow:'hidden' }}>
        <div className="veyl-blob-b" style={blob('320px','320px','rgba(75,44,94,0.22)',  { top:'0%',  right:'-60px' })} />
        <div className="veyl-blob-a" style={blob('380px','380px','rgba(0,128,129,0.18)', { top:'40%', left:'-80px' })} />
        <div style={{ maxWidth:'800px', margin:'0 auto', position:'relative', zIndex:1 }}>
          <h2 id="veyl-goals-title" style={{ fontSize:'clamp(1.75rem,4vw,2.5rem)', fontWeight:'800', color:'#fff', lineHeight:1.22, marginBottom:'.75rem' }}>{t('goalsTitle')}</h2>
          <p id="veyl-goals-sub" style={{ color:'rgba(255,255,255,0.55)', fontSize:'1.02rem', lineHeight:1.72, marginBottom:'2.5rem' }}>{t('goalsSub')}</p>
          <div className="veyl-goals-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:'.875rem' }}>
            {GOAL_CATEGORIES.map((g, i) => (
              <div key={i} className="veyl-goal-pill" style={{ backgroundColor:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'.875rem', padding:'1.1rem .875rem', textAlign:'center', fontSize:'.88rem', fontWeight:'500', color:'rgba(255,255,255,0.55)', transition:'all .2s', cursor:'default' }}>
                {g.emoji} {t(g.labelKey)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section style={{ padding:'5rem 1.5rem', position:'relative', overflow:'hidden', background:'linear-gradient(180deg,#080f1a 0%,#0a1520 50%,#080f1a 100%)' }}>
        <div className="veyl-blob-a" style={blob('380px','380px','rgba(0,128,129,0.18)', { top:'15%', left:'-80px' })} />
        <div className="veyl-blob-b" style={blob('320px','320px','rgba(75,44,94,0.22)',  { top:'55%', right:'-60px' })} />
        <div style={{ maxWidth:'900px', margin:'0 auto', position:'relative', zIndex:1 }}>
          <div style={{ textAlign:'center', marginBottom:'3.5rem' }}>
            <h2 id="veyl-price-title" style={{ fontSize:'clamp(1.75rem,4vw,2.5rem)', fontWeight:'800', color:'#fff', lineHeight:1.22 }}>{t('pricingTitle')}</h2>
          </div>
          <div className="veyl-pricing-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:'1.5rem' }}>
            <div className="veyl-price-card veyl-card" style={{ backgroundColor:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'1.25rem', padding:'2rem', transition:'transform .2s, box-shadow .2s' }}>
              <h3 style={{ fontSize:'1.25rem', fontWeight:'700', color:'#fff', marginBottom:'.5rem' }}>{t('freePlan')}</h3>
              <div style={{ display:'flex', alignItems:'baseline', gap:'.25rem', marginBottom:'1.5rem' }}>
                <span style={{ fontSize:'2.5rem', fontWeight:'800', color:'#fff' }}>$0</span>
                <span style={{ color:'rgba(255,255,255,0.4)', fontSize:'.9rem' }}>{t('foreverLabel')}</span>
              </div>
              <ul style={{ listStyle:'none', padding:0, marginBottom:'2rem', display:'flex', flexDirection:'column', gap:'.75rem' }}>
                {[t('freeFeature1'), t('freeFeature2'), t('freeFeature3'), t('freeFeature5')].map((f, i) => (
                  <li key={i} style={{ display:'flex', alignItems:'flex-start', gap:'.6rem', color:'rgba(255,255,255,0.65)', fontSize:'.9rem' }}>
                    <span style={{ color:'#008081', fontWeight:'700', flexShrink:0 }}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <button className="veyl-btn-secondary" onClick={onGetStarted} style={{ width:'100%', padding:'.875rem', backgroundColor:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.8)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'.75rem', fontWeight:'600', cursor:'pointer', transition:'background-color .2s, transform .2s' }}>{t('getStarted')}</button>
            </div>
            <div className="veyl-price-card veyl-card" style={{ background:'linear-gradient(145deg,rgba(0,128,129,0.15) 0%,rgba(75,44,94,0.15) 100%)', border:'1px solid rgba(0,128,129,0.4)', borderRadius:'1.25rem', padding:'2rem', position:'relative', transition:'transform .2s, box-shadow .2s', boxShadow:'0 8px 30px rgba(0,128,129,0.15)' }}>
              <div style={{ position:'absolute', top:'-12px', left:'50%', transform:'translateX(-50%)', background:'linear-gradient(135deg,#008081,#4B2C5E)', color:'#fff', padding:'.3rem 1.25rem', borderRadius:'9999px', fontSize:'.7rem', fontWeight:'700', letterSpacing:'.08em', textTransform:'uppercase', whiteSpace:'nowrap' }}>✨ {t('popular')}</div>
              <h3 style={{ fontSize:'1.25rem', fontWeight:'700', color:'#fff', marginBottom:'.5rem' }}>{t('premiumPlan')}</h3>
              <div style={{ display:'flex', alignItems:'baseline', gap:'.25rem', marginBottom:'.25rem' }}>
                <span style={{ fontSize:'2.5rem', fontWeight:'800', color:'#00c8c8' }}>$5</span>
                <span style={{ color:'rgba(255,255,255,0.4)', fontSize:'.9rem' }}>{t('perMonth')}</span>
              </div>
              <p style={{ fontSize:'.8rem', color:'rgba(0,200,200,0.7)', marginBottom:'1.5rem' }}>{t('sixMonthDeal')}</p>
              <ul style={{ listStyle:'none', padding:0, marginBottom:'2rem', display:'flex', flexDirection:'column', gap:'.75rem' }}>
                {[t('premiumFeature1'), t('premiumFeature2'), t('premiumFeature3'), t('premiumFeature4')].map((f, i) => (
                  <li key={i} style={{ display:'flex', alignItems:'flex-start', gap:'.6rem', color:'rgba(255,255,255,0.82)', fontSize:'.9rem', fontWeight:'500' }}>
                    <span style={{ color:'#00c8c8', fontWeight:'700', flexShrink:0 }}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <button className="veyl-btn-primary" onClick={onGetStarted} style={{ width:'100%', padding:'.875rem', background:'linear-gradient(135deg,#008081,#005f5f)', color:'#fff', border:'none', borderRadius:'.75rem', fontWeight:'700', cursor:'pointer', fontSize:'.95rem', boxShadow:'0 4px 15px rgba(0,128,129,0.35)' }}>{t('getStartedPremium')} ✨</button>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding:'5rem 1.5rem', position:'relative', overflow:'hidden' }}>
        <div className="veyl-blob-b" style={blob('320px','320px','rgba(75,44,94,0.22)',  { top:'5%',  right:'-60px' })} />
        <div className="veyl-blob-a" style={blob('380px','380px','rgba(0,128,129,0.18)', { top:'50%', left:'-80px' })} />
        <div style={{ maxWidth:'700px', margin:'0 auto', position:'relative', zIndex:1 }}>
          <div style={{ textAlign:'center', marginBottom:'3rem' }}>
            <h2 id="veyl-faq-title" style={{ fontSize:'clamp(1.75rem,4vw,2.5rem)', fontWeight:'800', color:'#fff', lineHeight:1.22 }}>{t('faqTitle')}</h2>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'.75rem' }}>
            {faqs.map((faq, idx) => (
              <div key={idx} className="veyl-faq-item" style={{ backgroundColor:'rgba(255,255,255,0.03)', border:`1px solid ${openFaq === idx ? 'rgba(0,128,129,0.4)' : 'rgba(255,255,255,0.07)'}`, borderRadius:'.875rem', overflow:'hidden', transition:'border-color .2s' }}>
                <button className="veyl-faq-btn" onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  style={{ width:'100%', padding:'1.25rem 1.5rem', textAlign:'left', backgroundColor: openFaq === idx ? 'rgba(0,128,129,0.1)' : 'transparent', border:'none', cursor:'pointer', fontWeight:'600', color:'#fff', display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:'.95rem', lineHeight:1.5, transition:'background-color .2s' }}>
                  {faq.q}
                  <span style={{ fontSize:'1.25rem', color: openFaq === idx ? '#008081' : 'rgba(255,255,255,0.3)', transition:'transform .2s, color .2s', transform: openFaq === idx ? 'rotate(45deg)' : 'rotate(0deg)', flexShrink:0, marginLeft:'1rem' }}>+</span>
                </button>
                {openFaq === idx && (
                  <div style={{ padding:'0 1.5rem 1.25rem', color:'rgba(255,255,255,0.55)', fontSize:'.9rem', lineHeight:1.8, animation:'fadeIn .2s ease' }}>{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SAFE ── */}
      <section style={{ padding:'5rem 1.5rem', position:'relative', overflow:'hidden' }}>
        <div className="veyl-blob-a" style={blob('380px','380px','rgba(0,128,129,0.18)', { top:'10%', left:'-80px' })} />
        <div className="veyl-blob-b" style={blob('320px','320px','rgba(75,44,94,0.22)',  { top:'50%', right:'-60px' })} />
        <div style={{ maxWidth:'960px', margin:'0 auto', position:'relative', zIndex:1 }}>
          <h2 id="veyl-safe-title" style={{ fontSize:'clamp(1.75rem,4vw,2.5rem)', fontWeight:'800', color:'#fff', lineHeight:1.22, marginBottom:'3rem' }}>{t('safeTitle')}</h2>
          <div className="veyl-safe-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'1.25rem' }}>
            {[
              { titleKey:'safe1Title', descKey:'safe1Desc' },
              { titleKey:'safe2Title', descKey:'safe2Desc' },
              { titleKey:'safe3Title', descKey:'safe3Desc' },
              { titleKey:'safe4Title', descKey:'safe4Desc' },
            ].map((c, i) => (
              <div key={i} className="veyl-safe-card" style={{ backgroundColor:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'1rem', padding:'1.75rem 1.5rem', transition:'border-color .2s' }}>
                <h4 style={{ fontSize:'.95rem', fontWeight:'700', color:'#fff', marginBottom:'.5rem' }}>{t(c.titleKey)}</h4>
                <p style={{ color:'rgba(255,255,255,0.55)', fontSize:'.88rem', lineHeight:1.6, margin:0 }}>{t(c.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding:'5rem 1.5rem', textAlign:'center', position:'relative', overflow:'hidden' }}>
        <div style={blob('500px','500px','rgba(0,128,129,0.12)', { top:'50%', left:'50%', transform:'translate(-50%,-50%)' })} />
        <div className="veyl-blob-b" style={blob('300px','300px','rgba(75,44,94,0.18)',  { top:'20%', right:'10%' })} />
        <div style={{ position:'relative', zIndex:1, maxWidth:'600px', margin:'0 auto' }}>
          <h2 id="veyl-cta-title" style={{ fontSize:'clamp(1.75rem,4vw,2.75rem)', fontWeight:'800', color:'#fff', marginBottom:'1rem' }}>{t('ctaTitle')}</h2>
          <p id="veyl-cta-p" style={{ color:'rgba(255,255,255,0.55)', fontSize:'1.05rem', marginBottom:'2rem', lineHeight:1.7 }}>{t('ctaSubtitle')}</p>
          <div id="veyl-cta-btn">
            <button className="veyl-btn-primary" onClick={onGetStarted}
              style={{ padding:'1.1rem 3rem', background:'linear-gradient(135deg,#008081 0%,#005f5f 100%)', color:'#fff', border:'none', borderRadius:'.875rem', fontSize:'1.1rem', fontWeight:'700', cursor:'pointer', boxShadow:'0 8px 30px rgba(0,128,129,0.4)' }}>
              {t('getStarted')} →
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ padding:'2rem 1.5rem', borderTop:'1px solid rgba(255,255,255,0.06)', textAlign:'center' }}>
        <p style={{ color:'rgba(255,255,255,0.3)', fontSize:'.8rem', lineHeight:1.9 }}>
          © 2026 Veyl · veyl.space ·{' '}
          <a href="/privacy" style={{ color:'rgba(0,200,200,0.7)', textDecoration:'none' }}>{t('privacyPolicy')}</a>{' · '}
          <a href="/terms"   style={{ color:'rgba(0,200,200,0.7)', textDecoration:'none' }}>{t('termsOfService')}</a>{' · '}
          <a href="/refund"  style={{ color:'rgba(0,200,200,0.7)', textDecoration:'none' }}>{t('refundPolicy')}</a>{' · '}
          <a href="mailto:support@veyl.space" style={{ color:'rgba(0,200,200,0.7)', textDecoration:'none' }}>support@veyl.space</a>
        </p>
      </footer>
    </div>
  );
}